/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../utils/api";
import Swal from "sweetalert2";

const TicketModal = ({ isOpen, onClose, onSubmit, prefill }) => {
  const [step, setStep] = useState("profile");
  const [attorney, setAttorney] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCaseTypes, setLoadingCaseTypes] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [error, setError] = useState(null);

  // Form States
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [comment, setComment] = useState("");
  const [caseTypeId, setCaseTypeId] = useState("");
  const [formId, setFormId] = useState(prefill?.formId || "");
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  // Reset all state
  const resetState = useCallback(() => {
    setStep("profile");
    setAttorney(null);
    setReviews([]);
    setCaseTypes([]);
    setForms([]);
    setTitle("");
    setDescription("");
    setComment("");
    setCaseTypeId("");
    setFormId(prefill?.formId || "");
    setFiles([]);
    setFormErrors({});
    setIsSubmitting(false);
    setLoading(true);
    setError(null);
  }, [prefill?.formId]);

  useEffect(() => {
    if (!isOpen || !prefill?.attorneyId) {
      setLoading(false);
      return;
    }

    const fetchAttorneyData = async () => {
      try {
        const [profileRes, reviewsRes] = await Promise.all([
          api.get(`/lawyers/${prefill.attorneyId}`),
          api.get(`/lawyers/${prefill.attorneyId}`),
        ]);

        if (profileRes.data.success) setAttorney(profileRes.data.data);
        if (reviewsRes.data.success) setReviews(reviewsRes.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load attorney data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAttorneyData();
  }, [isOpen, prefill?.attorneyId]);

  // Fetch Case Types
  useEffect(() => {
    if (step !== "form" || !prefill?.attorneyId) return;

    const fetchCaseTypes = async () => {
      setLoadingCaseTypes(true);
      try {
        const res = await api.get(`/lawyers/${prefill.attorneyId}/case-types`);
        if (res.data.success) {
          setCaseTypes(res.data.data || []);
          if (res.data.data.length === 1) {
            setCaseTypeId(res.data.data[0].id.toString());
          }
        }
      } catch (err) {
        console.error("Failed to load case types:", err);
      } finally {
        setLoadingCaseTypes(false);
      }
    };

    fetchCaseTypes();
  }, [step, prefill?.attorneyId]);

  // Fetch Forms from /forms
  useEffect(() => {
    if (step !== "form") return;

    const fetchForms = async () => {
      setLoadingForms(true);
      try {
        const res = await api.get("/forms");
        if (res.data.success) {
          const activeForms = (res.data.data || []).filter(f => f.is_active);
          setForms(activeForms);

          // If prefill.formId exists and is valid, keep it
          if (prefill?.formId) {
            const valid = activeForms.some(f => f.id === parseInt(prefill.formId));
            if (!valid) setFormId(""); // Clear if not active
          }
        }
      } catch (err) {
        console.error("Failed to load forms:", err);
      } finally {
        setLoadingForms(false);
      }
    };

    fetchForms();
  }, [step, prefill?.formId]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      lastFocusedElementRef.current = document.activeElement;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const timeout = setTimeout(() => firstFieldRef.current?.focus(), 50);

    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isSubmitting) {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isSubmitting]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) handleClose();
  };

  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
    lastFocusedElementRef.current?.focus?.();
  }, [onClose, resetState]);

  const onFilesChange = (fileList) => {
    const newFiles = Array.from(fileList || []).slice(0, 10);
    setFiles(newFiles);
  };

  // Validation
  const validationErrors = useMemo(() => {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";
    if (!description.trim()) errs.description = "Description is required.";
    if (!caseTypeId) errs.caseTypeId = "Case type is required.";
    if (!formId) errs.formId = "Form is required.";
    if (files.length === 0) errs.files = "Attach at least one file.";
    return errs;
  }, [title, description, caseTypeId, formId, files]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validationErrors;
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("lawyer_id", prefill.attorneyId);
    formData.append("title", title.trim());
    formData.append("description", description.trim());
    if (comment.trim()) formData.append("comment", comment.trim());
    formData.append("case_type_id", caseTypeId);
    formData.append("form_id", formId);
    files.forEach((file) => formData.append("files[]", file));

    try {
      const response = await api.post("/requests", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        await Swal.fire({
          icon: "success",
          title: "Request Submitted!",
          text: "Your consultation request has been sent successfully.",
          confirmButtonText: "OK",
          confirmButtonColor: "#10b981",
        });
        onSubmit?.(response.data.data);
        handleClose();
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Submission failed. Please try again.";
      setFormErrors({ submit: msg });
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, y: 16, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 16, scale: 0.97 },
  };

  const StarRating = ({ rating }) => {
    const filled = Math.round(parseFloat(rating) * 2) / 2;
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((i) => {
          const isFull = filled >= i;
          const isHalf = !isFull && filled + 0.5 >= i;
          return (
            <svg
              key={i}
              className={`h-4 w-4 ${isFull || isHalf ? "text-amber-400" : "text-gray-300"}`}
              fill={isHalf ? "url(#half)" : "currentColor"}
              viewBox="0 0 20 20"
            >
              {isHalf && (
                <defs>
                  <linearGradient id="half">
                    <stop offset="50%" stopColor="currentColor" />
                    <stop offset="50%" stopColor="transparent" />
                  </linearGradient>
                </defs>
              )}
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          );
        })}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dialogRef}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={modalVariants}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={handleBackdropClick}
        >
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {step === "profile" ? "Attorney Profile" : "Request Consultation"}
              </h2>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Step 1: Profile */}
              {step === "profile" && (
                <div className="space-y-6">
                  {loading ? (
                    <div className="space-y-4">
                      <div className="h-6 bg-gray-200 rounded-full w-3/4 animate-pulse" />
                      <div className="h-4 bg-gray-200 rounded-full w-1/2 animate-pulse" />
                      <div className="h-20 bg-gray-100 rounded-xl animate-pulse" />
                    </div>
                  ) : error ? (
                    <div className="text-center py-8 text-red-600">{error}</div>
                  ) : attorney ? (
                    <>
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-xl font-bold text-blue-600">
                            {attorney.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{attorney.name}</h3>
                          <a href={`mailto:${attorney.email}`} className="text-sm text-blue-600 hover:underline">
                            {attorney.email}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <StarRating rating={attorney.average_rating} />
                        <span className="text-sm font-medium text-gray-700">
                          {attorney.average_rating} ({attorney.total_ratings} reviews)
                        </span>
                      </div>

                      {attorney.office && (
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                          <p className="font-semibold text-blue-700">{attorney.office.name}</p>
                          <p className="text-sm text-blue-600">
                            {attorney.office.city}, {attorney.office.state}
                          </p>
                          <p className="text-sm text-blue-600">{attorney.office.address}</p>
                          {attorney.office.phone && (
                            <a href={`tel:${attorney.office.phone}`} className="text-sm font-medium text-blue-700 hover:underline">
                              {attorney.office.phone}
                            </a>
                          )}
                        </div>
                      )}

                      {reviews.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Recent Reviews</h4>
                          <div className="space-y-3">
                            {reviews.slice(0, 3).map((review) => (
                              <div key={review.id} className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <StarRating rating={review.rating} />
                                  <span className="text-xs text-gray-500">
                                    {new Date(review.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                {review.comment && (
                                  <p className="text-sm text-gray-700">{review.comment}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={() => setStep("form")}
                          className="bg-blue-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition"
                        >
                          Get Consultation
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">No attorney data available.</div>
                  )}
                </div>
              )}

              {/* Step 2: Consultation Form */}
              {step === "form" && (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Title *</label>
                    <input
                      ref={firstFieldRef}
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Work Visa Application"
                      className={`mt-1.5 w-full rounded-xl border ${formErrors.title ? "border-rose-400" : "border-gray-200"} bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200`}
                      disabled={isSubmitting}
                    />
                    {formErrors.title && <p className="mt-1 text-xs text-rose-600">{formErrors.title}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Description *</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Explain your case in detail..."
                      className={`mt-1.5 w-full min-h-[100px] rounded-xl border ${formErrors.description ? "border-rose-400" : "border-gray-200"} bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200`}
                      disabled={isSubmitting}
                    />
                    {formErrors.description && <p className="mt-1 text-xs text-rose-600">{formErrors.description}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Case Type *</label>
                    <select
                      value={caseTypeId}
                      onChange={(e) => setCaseTypeId(e.target.value)}
                      className={`mt-1.5 w-full rounded-xl border ${formErrors.caseTypeId ? "border-rose-400" : "border-gray-200"} bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200`}
                      disabled={isSubmitting || loadingCaseTypes}
                    >
                      <option value="">Select case type...</option>
                      {caseTypes.map((ct) => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name} ({parseFloat(ct.price).toFixed(2)} USD)
                        </option>
                      ))}
                    </select>
                    {loadingCaseTypes && <p className="mt-1 text-xs text-gray-500">Loading case types...</p>}
                    {formErrors.caseTypeId && <p className="mt-1 text-xs text-rose-600">{formErrors.caseTypeId}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Form *</label>
                    <select
                      value={formId}
                      onChange={(e) => setFormId(e.target.value)}
                      className={`mt-1.5 w-full rounded-xl border ${formErrors.formId ? "border-rose-400" : "border-gray-200"} bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200`}
                      disabled={isSubmitting || loadingForms}
                    >
                      <option value="">Select form...</option>
                      {forms.map((form) => (
                        <option key={form.id} value={form.id}>
                          {form.name}
                        </option>
                      ))}
                    </select>
                    {loadingForms && <p className="mt-1 text-xs text-gray-500">Loading forms...</p>}
                    {formErrors.formId && <p className="mt-1 text-xs text-rose-600">{formErrors.formId}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Additional Comment (optional)</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Any extra details..."
                      className="mt-1.5 w-full min-h-[90px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700">Attach Files * (Max 10)</label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={(e) => onFilesChange(e.target.files)}
                      disabled={isSubmitting}
                      className="mt-1.5 w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">PDF, DOC, DOCX, PNG, JPG. Max 10 files.</p>
                    {formErrors.files && <p className="mt-1 text-xs text-rose-600">{formErrors.files}</p>}
                    {files.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {files.map((file, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-center justify-between">
                            <span>{file.name} ({Math.round(file.size / 1024)} KB)</span>
                            <button
                              type="button"
                              onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                              className="text-rose-600 hover:text-rose-700"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {formErrors.submit && <p className="text-sm text-rose-600">{formErrors.submit}</p>}

                  <div className="f-col flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setStep("profile")}
                      disabled={isSubmitting}
                      className="px-5 py-2.5 text-gray-700 bg-gray-100 rounded-full font-medium hover:bg-gray-200 disabled:opacity-50"
                    >
                      Back to Profile
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || loadingCaseTypes || loadingForms}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 disabled:opacity-70 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        "Submit Request"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TicketModal;